import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { DashboardComponent } from './routes/dashboard/dashboard.component';
import { SignInComponent } from './routes/sign-in/sign-in.component';
import { LoggedOutGuard } from './guards/logged-out.guard';
import { SystemSettingsComponent } from './routes/system-settings/system-settings.component';
import { ManageStaffComponent } from './routes/manage-staff/manage-staff.component';
import { CourseCatalogComponent } from './routes/manage-courses/components/course-catalog/course-catalog.component';
import { ProfileComponent } from './routes/profile/profile.component';
import { ValidationComponent } from './routes/manage-courses/components/validation/validation.component';
import { CourseRequestsComponent } from './routes/manage-courses/components/course-requests/course-requests.component';
import { ManageCoursesComponent } from './routes/manage-courses/manage-courses.component';
import { ValidateCoursesGuard } from './guards/validate-courses.guard';
import { SystemSettingsGuard } from './guards/system-settings.guard';
import { ManageStaffGuard } from './guards/manage-staff.guard';
import { CourseRequestsGuard } from './guards/course-requests.guard';
import { BulkUserUpdateGuard } from './guards/bulk-user-update.guard';
import { StateDocumentExportComponent } from './routes/manage-courses/components/state-document-export/state-document-export.component';
import { PermissionGuard } from './guards/permission.guard';
import { FromMichiganGuard } from './guards/from-michigan.guard';
import { SurveysComponent } from './routes/surveys/surveys.component';
import { PostsComponent } from './routes/bulletin-board/posts.component';
import { WaitlistsComponent } from './routes/manage-courses/components/waitlists/waitlists.component';
import { WaitlistComponent } from './routes/manage-courses/components/waitlists/waitlist/waitlist.component';
import { SsoCallbackComponent } from './routes/sign-in/components/sso-callback/sso-callback.component';
import { ViewPostComponent } from './routes/bulletin-board/view-post/view-post.component';
import { EditPostComponent } from './routes/bulletin-board/edit-post/edit-post.component';
import { PERMISSION_KIDS } from './guards/permission.guard';
import { ReportsModule } from './routes/reports/reports.module';

const routes: Routes = [
  {
    path: 'reports',
    loadChildren: () =>
      import('./routes/reports/reports.module').then((m) => m.ReportsModule),
    canActivate: [AuthGuard, PermissionGuard(PERMISSION_KIDS.RUN_DATA_REPORTS)],
  },
  {
    path: 'signin',
    component: SignInComponent,
    canActivate: [LoggedOutGuard],
  },
  { path: 'sso-callback', component: SsoCallbackComponent },
  {
    path: 'system-settings',
    component: SystemSettingsComponent,
    canActivate: [
      AuthGuard,
      PermissionGuard([
        'SYS_SETTINGS_DEFAULTS',
        'SYS_SETTINGS_TYPES',
        'SYS_SETTINGS_CATEGORIES',
        'SYS_SETTINGS_REQUIREMENTS',
        'SYS_SETTINGS_BUILDINGS',
        'SYS_SETTINGS_TAGS',
        'SYS_SETTINGS_ROLES',
        'SYS_SETTINGS_POSITIONS',
      ]),
    ],
  },
  {
    path: 'surveys',
    component: SurveysComponent,
    canActivate: [AuthGuard, PermissionGuard(PERMISSION_KIDS.MANAGE_SURVEYS)],
  },
  {
    path: 'bulletin-board',
    component: PostsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'bulletin-board/new',
    component: EditPostComponent,
    canActivate: [
      AuthGuard,
      PermissionGuard(PERMISSION_KIDS.EDIT_BULLETIN_BOARD),
    ],
  },
  {
    path: 'bulletin-board/:id',
    component: ViewPostComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'bulletin-board/:id/edit',
    component: EditPostComponent,
    canActivate: [
      AuthGuard,
      PermissionGuard(PERMISSION_KIDS.EDIT_BULLETIN_BOARD),
    ],
  },
  // {
  //   path: 'dashboard', component: DashboardComponent, canActivate: [ AuthGuard ],
  // },
  // TODO - add above line back in and remove lower when dashboard is ready
  { path: 'dashboard', redirectTo: '/profile' },
  {
    path: 'profile/:id',
    component: ProfileComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'manage-staff',
    component: ManageStaffComponent,
    canActivate: [AuthGuard, ManageStaffGuard],
  },
  {
    path: 'manage-courses',
    canActivate: [AuthGuard],
    children: [
      {
        path: 'index',
        component: ManageCoursesComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'validation',
        component: ValidationComponent,
        canActivate: [AuthGuard, PermissionGuard('VALIDATE_ATTENDANCE')],
      },
      {
        path: 'course-catalog',
        component: CourseCatalogComponent,
        canActivate: [AuthGuard],
      },
      {
        path: 'course-requests',
        component: CourseRequestsComponent,
        canActivate: [
          AuthGuard,
          PermissionGuard(PERMISSION_KIDS.COURSE_REQUESTS),
        ],
      },
      {
        path: 'document-export',
        component: StateDocumentExportComponent,
        canActivate: [
          AuthGuard,
          FromMichiganGuard,
          PermissionGuard('EXPORT_STATE_DOC'),
        ],
      },
      {
        path: 'waitlists',
        component: WaitlistsComponent,
        canActivate: [
          AuthGuard,
          PermissionGuard(PERMISSION_KIDS.MANAGE_WAITLISTS),
        ],
      },
      {
        path: 'waitlists/:id',
        component: WaitlistComponent,
        canActivate: [
          AuthGuard,
          PermissionGuard(PERMISSION_KIDS.MANAGE_WAITLISTS),
        ],
      },
    ],
  },
  {
    path: 'bulk-user-update',
    loadChildren: () =>
      import('./bulk-user-update/bulk-user-update.module').then((m) => m.BulkUserUpdateModule),
    canActivate: [AuthGuard, BulkUserUpdateGuard],
  },
  {
    path: 'home',
    redirectTo: '/profile',
    pathMatch: 'full',
  },
  {
    path: '',
    redirectTo: '/signin',
    pathMatch: 'full',
  },
  { path: '**', redirectTo: '/signin' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule { }
